import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, unwrap } from "@/lib/api";

const blankTask = () => ({ code: "new_task", label_en: "New task", label_es: "Nueva tarea", price: 10, is_active: true });

export default function AdminPricing() {
  const [rule, setRule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/pricing/active").then((r) => setRule(unwrap(r))).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const updateField = (key, val) => setRule((r) => ({ ...r, [key]: val }));
  const updateTask = (i, key, val) => setRule((r) => ({
    ...r,
    tasks: r.tasks.map((t, idx) => idx === i ? { ...t, [key]: val } : t),
  }));
  const removeTask = (i) => setRule((r) => ({ ...r, tasks: r.tasks.filter((_, idx) => idx !== i) }));
  const addTask = () => setRule((r) => ({ ...r, tasks: [...(r.tasks || []), blankTask()] }));

  const updateBreakpoint = (i, key, val) => setRule((r) => ({
    ...r,
    sqft_breakpoints: r.sqft_breakpoints.map((b, idx) => idx === i ? { ...b, [key]: Number(val) } : b),
  }));
  const removeBreakpoint = (i) => setRule((r) => ({ ...r, sqft_breakpoints: r.sqft_breakpoints.filter((_, idx) => idx !== i) }));
  const addBreakpoint = () => setRule((r) => ({ ...r, sqft_breakpoints: [...(r.sqft_breakpoints || []), { min: 0, max: 0, multiplier: 1 }] }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: `Pricing v${Date.now().toString(36)}`,
        base_price: Number(rule.base_price),
        price_per_sqft: Number(rule.price_per_sqft),
        sqft_breakpoints: (rule.sqft_breakpoints || []).map((b) => ({ min: Number(b.min), max: Number(b.max), multiplier: Number(b.multiplier) })),
        bedroom_surcharge: Number(rule.bedroom_surcharge),
        bathroom_surcharge: Number(rule.bathroom_surcharge),
        half_bathroom_surcharge: Number(rule.half_bathroom_surcharge),
        tasks: (rule.tasks || []).map((t) => ({
          code: t.code, label_en: t.label_en, label_es: t.label_es,
          price: Number(t.price), is_active: t.is_active !== false,
        })),
        minimum_order: Number(rule.minimum_order),
        commercial_base_price: Number(rule.commercial_base_price),
        commercial_price_per_sqft: Number(rule.commercial_price_per_sqft),
        commercial_max_sqft_auto_quote: Number(rule.commercial_max_sqft_auto_quote),
      };
      await api.post("/admin/pricing/rules", payload);
      toast.success("Pricing saved — a new active version was created.");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !rule) return <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-pricing">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter">Pricing</h1>
          <p className="text-[#4B5563] text-sm mt-1">Active rule: <span className="font-mono">{rule.name}</span></p>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="save-pricing-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save new version</>}
        </Button>
      </div>

      <Card className="rounded-2xl p-6 border border-[#E5E7EB] grid grid-cols-2 md:grid-cols-3 gap-4">
        <Field label="Base price ($)" value={rule.base_price} onChange={(v) => updateField("base_price", v)} testid="price-base" />
        <Field label="Per sq ft ($)" value={rule.price_per_sqft} step="0.01" onChange={(v) => updateField("price_per_sqft", v)} testid="price-per-sqft" />
        <Field label="Bedroom surcharge ($)" value={rule.bedroom_surcharge} onChange={(v) => updateField("bedroom_surcharge", v)} testid="price-bedroom" />
        <Field label="Bathroom surcharge ($)" value={rule.bathroom_surcharge} onChange={(v) => updateField("bathroom_surcharge", v)} testid="price-bathroom" />
        <Field label="Half-bath surcharge ($)" value={rule.half_bathroom_surcharge} onChange={(v) => updateField("half_bathroom_surcharge", v)} testid="price-halfbath" />
        <Field label="Minimum order ($)" value={rule.minimum_order} onChange={(v) => updateField("minimum_order", v)} testid="price-min" />
      </Card>

      <Card className="rounded-2xl p-6 border border-[#E5E7EB]">
        <h3 className="font-display font-bold mb-1">Other spaces / commercial</h3>
        <p className="text-xs text-[#4B5563] mb-4">Used only when the customer picks "Other space" (warehouses, offices, units, single rooms) — completely separate from the residential rate above.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Field label="Commercial base price ($)" value={rule.commercial_base_price} onChange={(v) => updateField("commercial_base_price", v)} testid="price-commercial-base" />
          <Field label="Commercial per sq ft ($)" value={rule.commercial_price_per_sqft} step="0.01" onChange={(v) => updateField("commercial_price_per_sqft", v)} testid="price-commercial-per-sqft" />
          <Field label="Auto-quote up to (sq ft)" value={rule.commercial_max_sqft_auto_quote} step="1" onChange={(v) => updateField("commercial_max_sqft_auto_quote", v)} testid="price-commercial-max-sqft" />
        </div>
      </Card>

      <Card className="rounded-2xl p-6 border border-[#E5E7EB]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold">Sq ft breakpoints</h3>
          <Button onClick={addBreakpoint} variant="outline" size="sm" className="rounded-xl" data-testid="add-breakpoint">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
        <div className="space-y-2">
          {(rule.sqft_breakpoints || []).map((b, i) => (
            <div key={i} className="grid grid-cols-3 sm:grid-cols-4 gap-2 items-end">
              <Field label="Min" value={b.min} onChange={(v) => updateBreakpoint(i, "min", v)} testid={`bp-${i}-min`} />
              <Field label="Max" value={b.max} onChange={(v) => updateBreakpoint(i, "max", v)} testid={`bp-${i}-max`} />
              <Field label="Multiplier" value={b.multiplier} step="0.01" onChange={(v) => updateBreakpoint(i, "multiplier", v)} testid={`bp-${i}-mult`} />
              <Button onClick={() => removeBreakpoint(i)} variant="outline" size="icon" className="rounded-xl h-10" data-testid={`bp-${i}-remove`}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="rounded-2xl p-6 border border-[#E5E7EB]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold">Tasks catalog</h3>
          <Button onClick={addTask} variant="outline" size="sm" className="rounded-xl" data-testid="add-task">
            <Plus className="w-4 h-4 mr-1" /> Add task
          </Button>
        </div>
        <div className="space-y-3">
          {(rule.tasks || []).map((tk, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-end p-3 rounded-xl bg-[#F5F5F4]">
              <div>
                <Label className="text-xs">Code</Label>
                <Input value={tk.code} onChange={(e) => updateTask(i, "code", e.target.value)} className="rounded-xl mt-1 font-mono" data-testid={`task-${i}-code`} />
              </div>
              <div>
                <Label className="text-xs">Label (EN)</Label>
                <Input value={tk.label_en} onChange={(e) => updateTask(i, "label_en", e.target.value)} className="rounded-xl mt-1" data-testid={`task-${i}-en`} />
              </div>
              <div>
                <Label className="text-xs">Label (ES)</Label>
                <Input value={tk.label_es} onChange={(e) => updateTask(i, "label_es", e.target.value)} className="rounded-xl mt-1" data-testid={`task-${i}-es`} />
              </div>
              <div>
                <Label className="text-xs">Price ($)</Label>
                <Input type="number" step="0.01" value={tk.price} onChange={(e) => updateTask(i, "price", e.target.value)}
                  className="rounded-xl mt-1 font-mono" data-testid={`task-${i}-price`} />
              </div>
              <Button onClick={() => removeTask(i)} variant="outline" size="icon" className="rounded-xl h-10" data-testid={`task-${i}-remove`}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      </Card>

      <PricingPreview />
    </div>
  );
}

function PricingPreview() {
  const [zones, setZones] = useState([]);
  const [zoneId, setZoneId] = useState("");
  const [propertyType, setPropertyType] = useState("apartment");
  const [sqft, setSqft] = useState(900);
  const [br, setBr] = useState(2);
  const [ba, setBa] = useState(1);
  const [hb, setHb] = useState(0);
  const [tasks, setTasks] = useState([]);
  const [activeRule, setActiveRule] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/zones").then((r) => {
      const list = unwrap(r) || [];
      setZones(list);
      if (list.length && !zoneId) setZoneId(list[0].id);
    }).catch(() => {});
    api.get("/pricing/active").then((r) => setActiveRule(unwrap(r))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!zoneId) return;
    setBusy(true);
    const id = setTimeout(async () => {
      try {
        const r = await api.post("/pricing/quote", {
          zone_id: zoneId, property_type: propertyType, square_feet: Number(sqft),
          bedrooms: Number(br), bathrooms: Number(ba), half_bathrooms: Number(hb),
          selected_tasks: tasks,
        });
        setBreakdown(unwrap(r).breakdown);
      } catch { /* ignore */ } finally { setBusy(false); }
    }, 200);
    return () => clearTimeout(id);
  }, [zoneId, propertyType, sqft, br, ba, hb, tasks]);

  const toggleTask = (code) => setTasks((t) => t.includes(code) ? t.filter((c) => c !== code) : [...t, code]);

  return (
    <Card className="rounded-2xl p-6 border border-[#0EA5A4]/30 bg-[#0EA5A4]/[0.03]" data-testid="pricing-preview">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-8 h-8 rounded-lg bg-[#0EA5A4] grid place-items-center text-white"><Loader2 className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /></span>
        <h3 className="font-display font-bold">Preview pricing simulator</h3>
        <span className="text-xs text-[#4B5563] ml-2">Tests the LIVE active rule — save your changes above first.</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Zone</Label>
            <select value={zoneId} onChange={(e) => setZoneId(e.target.value)}
              className="mt-1 w-full h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm" data-testid="preview-zone">
              {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Property type</Label>
            <select value={propertyType} onChange={(e) => setPropertyType(e.target.value)}
              className="mt-1 w-full h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm" data-testid="preview-property">
              <option value="apartment">Apartment</option>
              <option value="house">House</option>
              <option value="studio">Studio</option>
              <option value="condo">Condo</option>
              <option value="other">Other space (commercial)</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Sq ft: {sqft}</Label>
              <input type="range" min="200" max="10000" step="50" value={sqft} onChange={(e) => setSqft(Number(e.target.value))}
                className="w-full accent-[#0EA5A4] mt-2" data-testid="preview-sqft" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Bedrooms</Label>
              <Input type="number" value={br} onChange={(e) => setBr(Number(e.target.value) || 0)} className="rounded-xl mt-1 font-mono" data-testid="preview-br" />
            </div>
            <div><Label className="text-xs">Baths</Label>
              <Input type="number" value={ba} onChange={(e) => setBa(Number(e.target.value) || 0)} className="rounded-xl mt-1 font-mono" data-testid="preview-ba" />
            </div>
            <div><Label className="text-xs">½ Baths</Label>
              <Input type="number" value={hb} onChange={(e) => setHb(Number(e.target.value) || 0)} className="rounded-xl mt-1 font-mono" data-testid="preview-hb" />
            </div>
          </div>
          {activeRule && (
            <div>
              <Label className="text-xs">Extras</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {(activeRule.tasks || []).filter(t => t.is_active !== false).map((tk) => {
                  const active = tasks.includes(tk.code);
                  return (
                    <button key={tk.code} onClick={() => toggleTask(tk.code)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? "bg-[#0EA5A4] text-white border-[#0EA5A4]" : "bg-white border-[#E5E7EB] hover:border-[#0EA5A4]"}`}
                      data-testid={`preview-task-${tk.code}`}>
                      {tk.label_en} <span className="opacity-70 font-mono">+${tk.price.toFixed(0)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
          <div className="text-xs uppercase tracking-wider text-[#4B5563] font-semibold mb-3">Live breakdown</div>
          {breakdown ? (
            <dl className="text-sm space-y-1">
              <Row label="Base" value={breakdown.base_price} />
              <Row label="Size" value={breakdown.sqft_surcharge} />
              <Row label="Bedrooms" value={breakdown.bedroom_surcharge} />
              <Row label="Bathrooms" value={breakdown.bathroom_surcharge} />
              <Row label="½ Baths" value={breakdown.half_bathroom_surcharge} />
              <Row label="Extras" value={breakdown.tasks_surcharge} />
              <Row label="Zone fee" value={breakdown.zone_surcharge} />
              <div className="flex justify-between border-t border-[#E5E7EB] pt-2 mt-2">
                <dt className="font-display font-bold">Total</dt>
                <dd className="font-mono text-2xl font-bold text-[#0EA5A4]" data-testid="preview-total">${breakdown.total.toFixed(2)}</dd>
              </div>
            </dl>
          ) : <div className="py-6 text-sm text-[#9CA3AF] text-center">Pick a zone to start</div>}
        </div>
      </div>
    </Card>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between"><dt className="text-[#4B5563]">{label}</dt><dd className="font-mono">${(value || 0).toFixed(2)}</dd></div>
  );
}

function Field({ label, value, step = "0.01", onChange, testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type="number" step={step} value={value} onChange={(e) => onChange(e.target.value)}
        className="rounded-xl mt-1 font-mono" data-testid={testid} />
    </div>
  );
}
